import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { X, CookingPot, Link, Check } from '@phosphor-icons/react';

export function Modal({ onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }} onClick={onClose}>
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl p-5 sm:p-6"
        style={{ background: 'rgba(30,30,30,0.98)', border: '1px solid rgba(255,255,255,0.1)' }}
        onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

export function InputField({ label, value, onChange, placeholder, testId, type = 'text', required }) {
  return (
    <div>
      <label className="text-xs font-semibold text-neutral-500 uppercase block mb-1">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} required={required}
        data-testid={testId} placeholder={placeholder}
        className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-sm outline-none focus:border-[#FF6B35]/50" />
    </div>
  );
}

export function TagInput({ values, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const add = () => { if (input.trim() && !values.includes(input.trim())) { onChange([...values, input.trim()]); setInput(''); } };
  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="flex-1 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm outline-none" />
        <button onClick={add} className="px-3 py-2.5 rounded-xl bg-white/5 text-sm font-semibold">Add</button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {values.map(v => (
          <span key={v} className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-semibold bg-red-500/10 text-red-400">
            {v} <button onClick={() => onChange(values.filter(x => x !== v))}><X size={10} /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export function StatCard({ label, value, color }) {
  return (
    <div className="rounded-2xl p-3 sm:p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-[10px] sm:text-xs uppercase tracking-wider text-neutral-500 mb-1">{label}</p>
      <p className="text-lg sm:text-2xl font-heading font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

export function Spinner() {
  return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" /></div>;
}

export function EmptyState({ text }) {
  return (
    <div className="text-center py-10 rounded-2xl col-span-full" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <p className="text-sm text-neutral-500">{text}</p>
    </div>
  );
}

export function EpLabel({ children }) {
  return <label className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 block mb-1">{children}</label>;
}

export function CookQRModal({ onClose }) {
  const [copied, setCopied] = useState(false);
  const cookUrl = `${window.location.origin}/cook-view`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(cookUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback */ }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cook-qr-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-sm rounded-3xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="px-6 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B35]/10 flex items-center justify-center">
              <CookingPot size={22} weight="fill" className="text-[#FF6B35]" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">Kitchen Display</h3>
              <p className="text-xs text-neutral-500">Scan to open on tablet</p>
            </div>
          </div>
          <button onClick={onClose} data-testid="cook-qr-close" className="w-8 h-8 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors">
            <X size={16} className="text-neutral-400" />
          </button>
        </div>

        <div className="px-6 py-6 flex flex-col items-center">
          <div className="p-4 rounded-2xl bg-white">
            <QRCodeSVG
              value={cookUrl}
              size={200}
              level="H"
              includeMargin={false}
              fgColor="#0A0A0A"
              bgColor="#FFFFFF"
              data-testid="cook-qr-code"
            />
          </div>
          <p className="text-sm text-neutral-400 mt-4 text-center">Point your tablet camera at this code</p>
          <p className="text-xs text-neutral-600 mt-1">Opens today's menu in Hindi</p>
        </div>

        <div className="px-6 pb-6">
          <button
            data-testid="copy-cook-link-btn"
            onClick={handleCopy}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold transition-all"
            style={{
              background: copied ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${copied ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.08)'}`,
              color: copied ? '#22C55E' : '#A3A3A3',
            }}
          >
            {copied ? <Check size={16} weight="bold" /> : <Link size={16} />}
            {copied ? 'Link Copied!' : 'Copy Link'}
          </button>
        </div>
      </div>
    </div>
  );
}
